import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Sparkles, Zap, Layers } from "lucide-react";

/**
 * FloatingHero3D — replaces the central neon ball.
 * Renders a stack of floating browser/UI mockup cards in 3D perspective,
 * a perspective grid floor, neon scan lines and ambient particles.
 * Designed to feel like a premium agency showroom.
 */

const Particle = ({ delay, duration, x, y, size }) => (
  <motion.span
    className="absolute rounded-full pointer-events-none"
    style={{
      left: `${x}%`,
      top: `${y}%`,
      width: size,
      height: size,
      background: "radial-gradient(circle, #DDD6FE 0%, transparent 70%)",
      filter: "blur(0.5px)",
    }}
    animate={{
      opacity: [0, 1, 0],
      y: [0, -25, 0],
    }}
    transition={{ duration, delay, repeat: Infinity, ease: "easeInOut" }}
  />
);

const FloatingCard = ({
  className,
  rotateX = 0,
  rotateY = 0,
  delay = 0,
  children,
  size = "md",
}) => {
  const sizes = {
    sm: "w-44 md:w-56",
    md: "w-56 md:w-72",
    lg: "w-64 md:w-80",
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.92 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className={`absolute ${sizes[size]} ${className}`}
      style={{
        transformStyle: "preserve-3d",
        transform: `perspective(1200px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`,
      }}
    >
      <motion.div
        animate={{ y: [0, -10, 0] }}
        transition={{
          duration: 5 + delay * 2,
          repeat: Infinity,
          ease: "easeInOut",
          delay,
        }}
        className="relative w-full rounded-2xl bg-gradient-to-br from-[#1a0a2e]/95 via-[#0d0418]/95 to-[#0a0314]/95 border border-violet-400/30 overflow-hidden shadow-[0_30px_70px_rgba(139,92,246,0.45),0_0_50px_rgba(139,92,246,0.25)_inset]"
      >
        {children}
      </motion.div>
    </motion.div>
  );
};

export const FloatingHero3D = ({ compact = false }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  const yA = useTransform(scrollYProgress, [0, 1], [0, -40]);
  const yB = useTransform(scrollYProgress, [0, 1], [0, 40]);
  const yC = useTransform(scrollYProgress, [0, 1], [0, -20]);

  return (
    <div
      ref={ref}
      data-testid="floating-hero-3d"
      className={`relative w-full h-full flex items-center justify-center overflow-visible ${
        compact ? "scale-[0.9]" : ""
      }`}
    >
      {/* Ambient neon glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 50% 55%, rgba(139, 92, 246, 0.45) 0%, rgba(139, 92, 246, 0.15) 35%, transparent 70%)",
          filter: "blur(20px)",
        }}
      />

      {/* Perspective grid floor */}
      <div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[140%] h-[55%] pointer-events-none"
        style={{
          perspective: "900px",
        }}
      >
        <div
          className="w-full h-full opacity-50"
          style={{
            transform: "rotateX(72deg)",
            transformOrigin: "center bottom",
            backgroundImage:
              "linear-gradient(rgba(167, 139, 250, 0.25) 1px, transparent 1px), linear-gradient(90deg, rgba(167, 139, 250, 0.25) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
            maskImage:
              "linear-gradient(to top, black 10%, transparent 90%)",
            WebkitMaskImage:
              "linear-gradient(to top, black 10%, transparent 90%)",
          }}
        />
      </div>

      {/* Glow halo behind cards */}
      <div
        className="absolute w-[60%] h-[60%] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(167, 139, 250, 0.35) 0%, rgba(139, 92, 246, 0.0) 70%)",
          filter: "blur(40px)",
        }}
      />

      {/* === 3D floating cards === */}

      {/* Back card — Analytics dashboard mockup */}
      <motion.div style={{ y: yC }} className="absolute inset-0 flex items-center justify-center z-10">
        <FloatingCard
          className="left-[2%] top-[8%]"
          rotateX={8}
          rotateY={-22}
          delay={0.3}
          size="md"
        >
          {/* Browser chrome */}
          <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/10 bg-black/40">
            <span className="w-2 h-2 rounded-full bg-red-400/60" />
            <span className="w-2 h-2 rounded-full bg-yellow-400/60" />
            <span className="w-2 h-2 rounded-full bg-green-400/60" />
          </div>
          <div className="p-4">
            <div className="text-[8px] uppercase tracking-[0.25em] text-violet-300/70 mb-2">
              Conversion · Live
            </div>
            <div className="font-display text-xl font-black neon-text">+38.4%</div>
            <div className="mt-3 h-12 flex items-end gap-1">
              {[18, 24, 16, 30, 22, 36, 28, 42, 34, 50].map((h, i) => (
                <motion.div
                  key={i}
                  initial={{ height: 0 }}
                  animate={{ height: `${h}px` }}
                  transition={{ delay: 0.6 + i * 0.06, duration: 0.5 }}
                  className="w-2 rounded-sm bg-gradient-to-t from-violet-700 to-violet-300"
                />
              ))}
            </div>
            <div className="mt-3 flex items-center justify-between">
              <div className="text-[9px] text-white/50 tracking-wider">
                7-day funnel
              </div>
              <Zap size={11} className="text-violet-300" />
            </div>
          </div>
        </FloatingCard>
      </motion.div>

      {/* Front-center card — Portfolio thumbnail (highest z-index) */}
      <motion.div style={{ y: yA }} className="absolute inset-0 flex items-center justify-center z-30">
        <FloatingCard
          className="left-1/2 -translate-x-1/2 top-[28%]"
          rotateX={4}
          rotateY={0}
          delay={0.1}
          size="lg"
        >
          <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/10 bg-black/40">
            <span className="w-2 h-2 rounded-full bg-red-400/60" />
            <span className="w-2 h-2 rounded-full bg-yellow-400/60" />
            <span className="w-2 h-2 rounded-full bg-green-400/60" />
            <div className="ml-2 flex-1 h-3 rounded-full bg-white/5 border border-white/10" />
          </div>
          <div className="relative aspect-[16/10] overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1639195276454-f0ec20b5bcb6?crop=entropy&cs=srgb&fm=jpg&w=900&q=80"
              alt="Portfolio mockup"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0314]/90 via-transparent to-transparent" />
            <div className="absolute bottom-3 left-3 right-3">
              <div className="text-[9px] uppercase tracking-[0.3em] text-violet-300 mb-1">
                Live Project
              </div>
              <div className="font-display text-sm md:text-base font-black uppercase">
                Nocturne Skincare
              </div>
            </div>
            {/* Scan line */}
            <motion.div
              className="absolute left-0 right-0 h-[2px]"
              style={{
                background:
                  "linear-gradient(90deg, transparent, #C4B5FD, transparent)",
                boxShadow: "0 0 12px rgba(196,181,253,0.8)",
              }}
              animate={{ top: ["-5%", "105%"] }}
              transition={{ duration: 3.6, repeat: Infinity, ease: "linear" }}
            />
          </div>
        </FloatingCard>
      </motion.div>

      {/* Right card — Stat tile */}
      <motion.div style={{ y: yB }} className="absolute inset-0 flex items-center justify-center z-20">
        <FloatingCard
          className="right-[1%] top-[10%]"
          rotateX={6}
          rotateY={24}
          delay={0.5}
          size="sm"
        >
          <div className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-lg bg-violet-500/25 border border-violet-400/40 flex items-center justify-center">
                <Layers size={13} className="text-violet-200" />
              </div>
              <div className="text-[8px] uppercase tracking-[0.25em] text-white/50">
                Brand System
              </div>
            </div>
            <div className="font-display text-lg font-black neon-text leading-none">
              60+ Brands
            </div>
            <div className="text-[9px] text-white/50 tracking-wider mt-1">
              Forged · 5 years
            </div>
            <div className="mt-3 grid grid-cols-3 gap-1">
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <div
                  key={n}
                  className="aspect-square rounded-md bg-gradient-to-br from-violet-500/30 to-violet-900/30 border border-violet-400/20"
                />
              ))}
            </div>
          </div>
        </FloatingCard>
      </motion.div>

      {/* Bottom-right floating chip */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.1, duration: 0.7 }}
        className="absolute bottom-[6%] right-[8%] z-20 inline-flex items-center gap-2 bg-black/80 backdrop-blur border border-violet-400/40 px-3 py-2 rounded-full"
        style={{ boxShadow: "0 0 25px rgba(139, 92, 246, 0.45)" }}
      >
        <Sparkles size={11} className="text-violet-300" />
        <span className="text-[10px] uppercase tracking-[0.3em] text-violet-200 font-semibold">
          Live · Crafted by Obsidian
        </span>
      </motion.div>

      {/* Top-left floating tag */}
      <motion.div
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.9, duration: 0.7 }}
        className="absolute top-[6%] left-[10%] z-20 inline-flex items-center gap-2 bg-violet-500/10 backdrop-blur border border-violet-400/40 px-3 py-1.5 rounded-full"
      >
        <span className="w-1.5 h-1.5 rounded-full bg-violet-300 animate-pulse" />
        <span className="text-[10px] uppercase tracking-[0.3em] text-violet-200 font-semibold">
          ★ 5.0 / 60+ Brands
        </span>
      </motion.div>

      {/* Particles */}
      <Particle delay={0} duration={5} x={20} y={30} size={4} />
      <Particle delay={1.2} duration={6} x={80} y={20} size={3} />
      <Particle delay={2.1} duration={5.5} x={75} y={70} size={5} />
      <Particle delay={0.6} duration={7} x={15} y={75} size={4} />
      <Particle delay={3} duration={6} x={50} y={15} size={3} />
      <Particle delay={1.8} duration={5} x={45} y={85} size={4} />
      <Particle delay={2.5} duration={6.5} x={88} y={45} size={3} />
      <Particle delay={0.4} duration={5.5} x={8} y={50} size={4} />
    </div>
  );
};

export default FloatingHero3D;
